/**
 * Sorts an array of objects by a specific key
 * @param data Array of objects to sort
 * @param key Object key to sort by
 * @param direction 'asc' for ascending, 'desc' for descending
 * @returns Sorted array
 */
export function sortData<T>(data: T[], key: keyof T, direction: 'asc' | 'desc'): T[] {
  // Handle case when data is not an array
  if (!Array.isArray(data)) {
    console.error('sortData: data is not an array', data);
    return [];
  }
  
  const sortedData = [...data];
  
  sortedData.sort((a, b) => {
    const valueA = a[key];
    const valueB = b[key];
    
    // Handle null values
    if (valueA === null || valueA === undefined) return direction === 'asc' ? -1 : 1;
    if (valueB === null || valueB === undefined) return direction === 'asc' ? 1 : -1;
    
    // Compare based on types
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return direction === 'asc' 
        ? valueA.localeCompare(valueB) 
        : valueB.localeCompare(valueA);
    } else if (typeof valueA === 'number' && typeof valueB === 'number') {
      return direction === 'asc' ? valueA - valueB : valueB - valueA;
    } else if (valueA instanceof Date && valueB instanceof Date) {
      return direction === 'asc' 
        ? valueA.getTime() - valueB.getTime() 
        : valueB.getTime() - valueA.getTime();
    } else if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
      return direction === 'asc'
        ? (valueA ? 1 : 0) - (valueB ? 1 : 0)
        : (valueB ? 1 : 0) - (valueA ? 1 : 0);
    }
    
    // Default comparison
    return direction === 'asc'
      ? String(valueA).localeCompare(String(valueB))
      : String(valueB).localeCompare(String(valueA));
  });
  
  return sortedData;
}

/**
 * Filters an array of objects based on a search term
 * @param data Array of objects to filter
 * @param searchTerm Term to search for
 * @param keys Object keys to search in
 * @returns Filtered array
 */
export function filterData<T>(data: T[], searchTerm: string, keys: (keyof T)[]): T[] {
  // Handle case when data is not an array
  if (!Array.isArray(data)) {
    console.error('filterData: data is not an array', data);
    return [];
  }
  
  if (!searchTerm || searchTerm.trim() === '') {
    return data;
  }
  
  const term = searchTerm.toLowerCase().trim();
  
  return data.filter(item => {
    return keys.some(key => {
      const value = item[key];
      if (value === null || value === undefined) return false;
      
      return String(value).toLowerCase().includes(term);
    });
  });
}

/**
 * Type for sort state
 */
export interface SortState<T> {
  key: keyof T;
  direction: 'asc' | 'desc';
} 